import React, { useEffect, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { SnackbarProvider } from 'notistack';
import { GrowthBookProvider, GrowthBook } from '@growthbook/growthbook-react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { SSOProvider } from '@vbee-holding/sso-pkg';
import { Box, CircularProgress } from '@mui/material';

import apis from '@src/apis';
import store from './redux/store';
import AppRouter from './router';
import theme from './styles/theme';
import {
  GB_API_HOST,
  GB_CLIENT_KEY,
  RECAPTCHA_V3_SITE_KEY,
  CLIENT_ID,
  SSO_URL,
  SSO_API_URL,
  MY_ACCOUNT_URL,
  ENV,
  DSN_SENTRY_URL,
} from './configs';
import NotiHandler from './errors/NotiHandler';
import FontLoader from './containers/FontLoader';
import useFeatureFlags from './hooks/useFeatureFlags';
import { FEATURE_KEYS } from './configs/featureKeys';
import {
  initDatadogRum,
  setDatadogUser,
  clearDatadogUser,
} from './services/datadog';
import { getDomain } from './utils/url';
import { updateLocation } from './redux/location/actions';
import { COUNTRY } from './constants/location';
import useLocation from './hooks/useLocation';

const growthbook = new GrowthBook({
  apiHost: GB_API_HOST,
  clientKey: GB_CLIENT_KEY,
  enableDevMode: true,
  subscribeToChanges: true,
});

const LocationProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { isFeatureFlagsReady } = useFeatureFlags();

  const [isLoading, setIsLoading] = useState(true);

  const fetchCountry = async () => {
    const res = await apis.country.getCountry();
    dispatch(
      updateLocation({
        ip: res?.ip,
        country: res?.country || COUNTRY.VN,
      }),
    );
    setIsLoading(false);
  };

  useEffect(() => {
    fetchCountry();
  }, []);

  // Wait for feature flags to be ready and location to be fetched before rendering the children components
  if (isLoading || !isFeatureFlagsReady) {
    return (
      <Box
        height="100vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return children;
};

const notistackRef = React.createRef();

const SSOProviderWrapper = () => {
  const { getFeatureValue } = useFeatureFlags();
  const useSentry = getFeatureValue(FEATURE_KEYS.SENTRY_AIVOICE_WEB_APP);
  const authUser = useSelector((state) => state.auth?.user);
  const { ip } = useLocation();

  const useDatadog = getFeatureValue(FEATURE_KEYS.DATADOG_AIVOICE_WEB_APP, {
    userId: authUser?.id,
    email: authUser?.email,
    phoneNumber: authUser?.phoneNumber,
    packageCode: authUser?.packageCode,
    ip,
  });
  const configSSO = {
    clientId: CLIENT_ID,
    ssoUrl: SSO_URL,
    ssoApiUrl: SSO_API_URL,
    myAccountUrl: MY_ACCOUNT_URL,
    clientDomain: getDomain(),
    options: {
      autoLogin: true,
      useSSO: true,
    },
  };

  if (useSentry) {
    Sentry.init({
      environment: ENV,
      dsn: DSN_SENTRY_URL,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: useSentry?.tracesSampleRate || 1.0,
      replaysSessionSampleRate: useSentry?.replaysSessionSampleRate || 0.1,
      replaysOnErrorSampleRate: useSentry?.replaysOnErrorSampleRate || 1.0,
    });
  }

  if (useDatadog?.enabled && authUser?.id) {
    initDatadogRum({
      sessionSampleRate: useDatadog?.sessionSampleRate,
      sessionReplaySampleRate: useDatadog?.sessionReplaySampleRate,
      defaultPrivacyLevel: useDatadog?.defaultPrivacyLevel,
      reactPluginEnabled: useDatadog?.reactPluginEnabled,
      reactPluginRouter: useDatadog?.reactPluginRouter,
      trackUserInteractions: useDatadog?.trackUserInteractions,
      trackResources: useDatadog?.trackResources,
      trackLongTasks: useDatadog?.trackLongTasks,
    });
  }

  useEffect(() => {
    if (!useDatadog?.enabled) return;
    if (authUser && authUser.id) {
      const { username, email, id } = authUser;
      setDatadogUser({ id, email, username });
    } else {
      clearDatadogUser();
    }
  }, [useDatadog, authUser?.id, authUser?.email, authUser?.username]);

  return (
    <SSOProvider config={configSSO}>
      <AppRouter />
    </SSOProvider>
  );
};

const App = () => {
  useEffect(() => {
    // Load features asynchronously when the app renders
    growthbook.loadFeatures();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GrowthBookProvider growthbook={growthbook}>
        <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_V3_SITE_KEY}>
          <Provider store={store()}>
            <LocationProvider>
              <SnackbarProvider
                preventDuplicate
                maxSnack={3}
                ref={notistackRef}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <NotiHandler>
                  <SSOProviderWrapper />
                </NotiHandler>
              </SnackbarProvider>
            </LocationProvider>
          </Provider>
        </GoogleReCaptchaProvider>
        <FontLoader />
      </GrowthBookProvider>
    </ThemeProvider>
  );
};

export default App;
